/**
 * My Library Text Constants
 * Centralized text strings for the My Library feature.
 */

// Page Title
export const PAGE_TITLE = "My Library";

// Action Buttons
export const BUTTON_UPLOAD_MEDIA = "Upload Media";
export const BUTTON_CREATE_PROJECT = "Create Project";

// Tab Labels
export const TAB_UPLOADED_MEDIA = "Uploaded Media";
export const TAB_EXPORTED = "Exported";

// Placeholder Messages
export const PLACEHOLDER_UPLOADS_CONTENT =
  "Uploaded Media content will be implemented here";
export const PLACEHOLDER_EXPORTS_CONTENT =
  "Exported content will be implemented here";

// A video that didn't become clips (task_status "failed"). Why is in its name: the worker writes
// "Over your free hour: …", "No sound found: …" or "Couldn't read this video: …" (2026-10-25).
export const FAILED_TEXT = "✕ Couldn't make clips";
export const FAILED_OVER_LIMIT_PREFIX = "Over your free hour: ";
export const FAILED_SEE_PLANS = "See plans";

// Task Status Display Text
export const TASK_STATUS_TEXT = {
  PENDING: "Pending…",
  TRANSCRIBING: "Transcribing…",
} as const;

// Empty State Messages
export const UPLOADS_EMPTY_STATE_MESSAGE =
  "No transcripts existing yet, when you upload a video it will appear here.";
export const EXPORTS_EMPTY_STATE_MESSAGE =
  "No exports existing yet, when you export a video it will appear here.";

// Action Button Labels
export const ACTION_DOWNLOAD = "Download";
export const ACTION_DELETE = "Delete";
export const ACTION_RENAME = "Rename";
export const ACTION_START_EDITING = "Open clips";
export const ACTION_TOP_CLIPS = "Top 20 clips";
export const ACTION_TOP_CLIPS_TITLE = "Top 20 clips: a short list of this video's most postable clips, to play, pick and export";
// Experimental (one recipe so far): kept in the ⋮ menu, off the row.
export const ACTION_AUTO_CLIP = "Auto-Clip (experimental)";
export const LIBRARY_HINT = "Click a video to open its clips. Every sentence is a clip you can play, pick and export.";
export const ACTION_RUN_WHISPER = "Run Whisper";
export const ACTION_REGENERATE_THUMBNAILS = "Regenerate thumbnails";
export const ACTION_REGENERATE_THUMBNAILS_QUEUING = "Queuing…";

// Rename Dialog
export const RENAME_DIALOG_TITLE = "Rename File";
export const RENAME_DIALOG_PLACEHOLDER_NAME = "File name";
export const RENAME_DIALOG_PLACEHOLDER_DESCRIPTION = "Description";
export const BUTTON_CANCEL = "Cancel";
export const BUTTON_SAVE = "Save";

// Delete Confirmation (reusable modal)
export const DELETE_CONFIRM_TITLE = "Delete file?";
export const getDeleteConfirmMessage = (fileName: string): string =>
  `Are you sure you want to delete "${fileName}" permanently?`;
export const BUTTON_CONFIRM = "Confirm";

// Error Messages
export const ERROR_DELETE_FAILED =
  "Failed to delete transcript. Please try again.";
export const ERROR_RENAME_FAILED =
  "Failed to rename transcript. Please try again.";
export const ERROR_DOWNLOAD_FAILED =
  "Failed to download video. Please try again.";
export const ERROR_LOAD_VIDEOS_FAILED =
  "Failed to load videos. Please try again.";

// Success Messages
export const SUCCESS_DELETE = "Transcript deleted successfully";
export const SUCCESS_RENAME = "Transcript renamed successfully";
export const SUCCESS_DOWNLOAD_STARTED = "Download started";
export const SUCCESS_COPY_TO_CLIPBOARD = "Copied to clipboard";

// Clipboard Error Messages
export const ERROR_COPY_TO_CLIPBOARD_FAILED = "Failed to copy to clipboard";

// Cursor Styles
export const CURSOR_POINTER = "pointer";
export const CURSOR_DEFAULT = "default";

// Route Paths
export const ROUTE_MY_LIBRARY = "/my-library/";
export const ROUTE_MY_LIBRARY_UPLOADS = "/my-library/uploads/";
export const ROUTE_MY_LIBRARY_EXPORTS = "/my-library/exports/";
export const ROUTE_MY_LIBRARY_SEARCH = "/my-library/search/";
export const ROUTE_MY_LIBRARY_CALENDAR = "/my-library/calendar/";
export const TAB_CALENDAR = "Calendar";
export const ROUTE_MY_LIBRARY_MEETINGS = "/my-library/meetings/";
export const TAB_MEETINGS = "Meetings";
export const TAB_SEARCH = "Search All Videos";

// Export status ("no cherry" = an export that failed or was given up on)
export const EXPORT_PROCESSING_TEXT = "Processing your clips… this will appear here automatically.";
export const EXPORT_GIVE_UP_LABEL = "✕ Give up";
export const EXPORT_GIVE_UP_TITLE = "Stop waiting for this export";
export const EXPORT_GIVE_UP_CONFIRM = "Give up on this export? It will be marked “no cherry” and the worker will stop working on it.";
export const EXPORT_NO_CHERRY = "No cherry";
export const EXPORT_FAILED_TEXT = "This export didn't make it. Delete it and try again.";
export const EXPORT_CANCELLED_TEXT = "You gave up on this export. Delete it whenever you like.";
export const EXPORT_DELETE_LABEL = "Delete";
export const EXPORT_CANCEL_ERROR = "Couldn't stop that export";

// Library search + sort
export const LIBRARY_SEARCH_PLACEHOLDER = "Search titles…";
export const LIBRARY_NO_MATCHES = "Nothing matches that search.";
export const LIBRARY_SORT_LABEL = "Sort";

// CherryClipz Auto — the 📤 Post button on a finished video (Account Settings → Auto)
export const AUTO_POST_TEXT = {
  POST: "📤 Post",
  POSTING: "Sending…",
  SENT: "✓ Sent",
  RETRY: "📤 Post again",
  POST_TITLE: "Send this video to your Auto (make.com) now.",
  WAITING_TITLE: "Waiting for you. Press Post to send it to your Auto (make.com).",
  SENT_TITLE: "Sent to your Auto. Press to send it again.",
  FAILED_TITLE: "The last send didn't go through. Press to try again.",
  SENT_MESSAGE: "Sent to your Auto.",
  FAILED_MESSAGE: "Couldn't send it to your Auto.",
} as const;
